import React, { useEffect, useState } from "react";
import { AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  GetBankFullNameByBankCode,
  getLPointShowValue,
  GetMoneyValue,
} from "../../../../Api/ApiCommon";
import LineBtn from "../../../Button/LineBtn";
import Blank from "../../../DefaultStyles/Blank";
import LineBox from "../../../DefaultStyles/LineBox";
import TitleWithLine from "../../../DefaultStyles/TitleWithLine";
import VerticalTable from "../../../Table/VerticalTable";
import CouponPointRegisterModal from "../../Benefit/Coupon/CouponPointRegisterModal";
import Flex from "../../../DefaultStyles/Flex";
import { useRecoilValue } from "recoil";
import { authType } from "../../../../pages/Login";
import UserImmediatePayModal from "./UserImmediatePayModal";
import SpaceBetween from "../../../DefaultStyles/SpaceBetween";
import UserPaydayPreviewModal from "./UserPaydayPreviewModal";
import BackAccountEditModal from "./BankEditModal/BackAccountEditModal";
import CouponUseModal from "../../Benefit/Coupon/CouponUseModal";

interface SLinkidMoneyProrityLabel {
  isOn: boolean;
}

interface UserDetailPayInfoProps {
  data?: {
    uid: string;
    name?: string;
    nickname?: string;
    payday?: number;
    money?: string;
    point?: string;
    cardname?: string;
    cardnumber?: string;
    bankname?: string;
    banknumber?: string;
    coupon?: number;
    usingcoupon?: string;
    changeBankDate?: string;
    changeCardDate?: string;
    usemoney?: number;
  };
  fetchData: (params: AxiosRequestConfig) => Promise<void>;
}

export default function UserDetailPayInfo({
  data,
  fetchData,
}: UserDetailPayInfoProps) {
  const auth = useRecoilValue(authType);

  const [paydayPreviewModal, setPaydayPreviewModal] = useState(false);
  const [pointInputModal, setPointInputModal] = useState(false);
  const [couponInputModal, setCouponInputModal] = useState(false);
  const [couponUseModal, setCouponUseModal] = useState(false);
  const [couponToastModal, setCouponToastModal] = useState(false);
  const [moneySettingModal, setMoneySettingModal] = useState(false);
  const [bankAccountEditModal, setbankAccountEditModal] = useState(false);
  const [userData, setUserData] = useState({
    uid: "",
    name: "",
    nickname: "",
  });

  const payInfoHeader = [
    "정산일",
    "링키드 머니",
    "링키드 포인트",
    "카드 정보",
    "계좌 정보",
    "보유 쿠폰",
  ];

  const saveUserData = () => {
    setUserData({
      uid: data?.uid ?? "",
      name: data?.name ?? "",
      nickname: data?.nickname ?? "",
    });
  };

  const payInfoBody = [
    [`${data?.payday ?? 0}일`],
    [
      <Flex>
        <>{GetMoneyValue((data?.money ?? 0) + "")}</>
        <LinkidMoneyProrityLabel isOn={data?.usemoney === 1 ? true : false}>
          {data?.usemoney === 1 ? "ON" : "OFF"}
        </LinkidMoneyProrityLabel>
      </Flex>,
      <LineBtn
        btnText="링키드 머니 관리"
        btnClickEvent={() => {
          saveUserData();
          setMoneySettingModal(true);
        }}
        disabled={!auth?.linkidMoney || !data?.money}
      />,
    ],
    [
      getLPointShowValue((data?.point ?? 0) + ""),
      <LineBtn
        btnText="지급/차감하기"
        btnClickEvent={() => {
          saveUserData();
          setPointInputModal(true);
        }}
        disabled={!data}
      />,
    ],
    [
      <>{data?.cardname ? `${data?.cardname} ${data?.cardnumber}` : "-"}</>,
      <EditDateText>
        {data?.changeCardDate &&
          `${dayjs(data?.changeCardDate)?.format(
            "YYYY-MM-DD HH:mm"
          )} 마지막 변경`}
      </EditDateText>,
    ],
    [
      <>
        {data?.bankname
          ? `${GetBankFullNameByBankCode(data?.bankname)} ${data?.banknumber}`
          : "-"}
      </>,
      <Flex>
        <EditDateText>
          {data?.changeBankDate &&
            `${dayjs(data?.changeBankDate)?.format(
              "YYYY-MM-DD HH:mm"
            )} 마지막 변경`}
        </EditDateText>
        <LineBtn
          btnText="수정하기"
          btnClickEvent={() => {
            setbankAccountEditModal(true);
          }}
          disabled={!data}
        />
      </Flex>,
    ],
    [
      data?.coupon === 0
        ? "0장"
        : `${data?.usingcoupon ?? 0}/${data?.coupon ?? 0}장`,
      <>
        <LineBtn
          btnText="지급하기"
          btnClickEvent={() => {
            saveUserData();
            setCouponInputModal(true);
          }}
          disabled={!data}
        />
        <LineBtn
          btnText="사용하기"
          btnClickEvent={() => {
            saveUserData();
            setCouponUseModal(true);
          }}
          disabled={!data}
        />
      </>,
    ],
  ];

  useEffect(() => {
    if (couponToastModal) {
      toast.success("쿠폰 지급이 완료되었습니다.", {
        onClose: () => setCouponToastModal(false),
      });
    }
  }, [couponToastModal]);

  return (
    <LineBox boxHeight="100%">
      <>
        <SpaceBetween>
          <TitleWithLine title="정산 정보" />

          <LineBtn
            btnText="링키드 정산일 미리보기"
            disabled={data?.payday === 0 || !data?.payday}
            btnClickEvent={() => setPaydayPreviewModal(true)}
          />
        </SpaceBetween>

        <Blank blankHeight={24} />

        <VerticalTable
          tableHeaderData={payInfoHeader}
          tableFlexBodyData={payInfoBody}
        />

        {/* 포인트 지급 모달 */}
        {pointInputModal && (
          <CouponPointRegisterModal
            closeEvent={() => setPointInputModal(false)}
            confirmEvent={() => {
              fetchData({
                method: "get",
                url: `user/${data?.uid}`,
              });
            }}
            isPoint={true}
            userData={userData}
          />
        )}

        {/* 쿠폰 지급 모달 */}
        {couponInputModal && (
          <CouponPointRegisterModal
            closeEvent={() => setCouponInputModal(false)}
            confirmEvent={() => {
              fetchData({
                method: "get",
                url: `user/${data?.uid}`,
              });
              setCouponToastModal(true);
            }}
            userData={userData}
          />
        )}

        {/* 쿠폰 적용 모달 */}
        {couponUseModal && (
          <CouponUseModal
            closeEvent={() => setCouponUseModal(false)}
            confirmEvent={() => {
              fetchData({
                method: "get",
                url: `user/${data?.uid}`,
              });
              setCouponToastModal(true);
            }}
            userData={userData}
          />
        )}

        {/* 링키드 머니 관리 모달 */}
        {moneySettingModal && (
          <UserImmediatePayModal
            closeEvent={() => setMoneySettingModal(false)}
            confirmEvent={() =>
              fetchData({
                method: "get",
                url: `user/${data?.uid ?? 0}`,
              })
            }
            userData={userData}
          />
        )}

        {paydayPreviewModal && (
          <UserPaydayPreviewModal
            payday={data?.payday ?? 0}
            closeEvent={() => setPaydayPreviewModal(false)}
          />
        )}

        {/* 계좌번호 수정 모달 */}
        {bankAccountEditModal && (
          <BackAccountEditModal
            closeEvent={() => setbankAccountEditModal(false)}
            confirmEvent={() =>
              fetchData({
                method: "get",
                url: `user/${data?.uid ?? 0}`,
              })
            }
            uid={Number(data?.uid)}
          />
        )}
      </>
    </LineBox>
  );
}

const EditDateText = styled.span`
  font-size: 13px;
  line-height: 19.5px;
  text-align: right;
  color: #858c97;
  word-break: keep-all;
`;

const LinkidMoneyProrityLabel = styled.label<SLinkidMoneyProrityLabel>`
  height: 23px;
  border-radius: 3px;
  background-color: #f5f5f5;
  padding: 4px 6px;
  margin: 0 10px;

  font-size: 12px;
  font-weight: bold;
  line-height: 18px;
  text-align: center;
  color: ${({ theme, isOn }) =>
    isOn ? theme.buttonColor.blueHover : theme.color.gray400};
`;
